/**
 * Turns the raw factors/metrics from GET /quotes/{quote_id}/competitiveness-score
 * (docs/engineering/design/quote_competitiveness_scoring_design.md §14) into the display
 * strings QuoteScoreBreakdown renders. The API stores exact numbers; only the
 * word-forms live here.
 *
 * Several lines below have no example in either Magic Patterns mockup (network
 * membership, a carrier with no reviews at all, the seven individual Profile
 * labels beyond the three the mockup showed) — those are this file's own best
 * guess at copy, not a design-of-record string, and are called out inline.
 */

// The carrier reads about their own quote; the shipper reads about a carrier
// quoting to them, so the relationship lines are worded from the other side.
export type QuoteScoreAudience = "carrier" | "shipper";

function getMetric(
  factor: QuoteScoreFactor,
  name: string,
): QuoteScoreMetric | undefined {
  return factor.metrics.find((metric) => metric.name === name);
}

export function formatPricePoints(priceFactor: QuoteScoreFactor): string[] {
  const points: string[] = [];
  const percentVsMedian = getMetric(priceFactor, "percent_vs_median");
  const otherQuoteCount = getMetric(priceFactor, "other_quote_count");

  // Redacted on the second quote on a load (§5) or absent entirely when Price
  // itself is unscoreable — either way, nothing to say about the median here.
  if (percentVsMedian != null && typeof percentVsMedian.value === "number") {
    const percent = percentVsMedian.value;
    if (percent === 0) {
      points.push("Exactly at the median");
    } else if (percent < 0) {
      points.push(`${Math.abs(percent)}% below the median`);
    } else {
      points.push(`${percent}% above the median`);
    }
  }

  // "Other", not "active": every quote on the load counts toward this,
  // regardless of status (§5) — including ones since declined or cancelled.
  if (otherQuoteCount != null && typeof otherQuoteCount.value === "number") {
    const count = otherQuoteCount.value;
    points.push(
      `${count} other ${count === 1 ? "quote" : "quotes"} on this load`,
    );
  }

  return points;
}

const HISTORY_WITH_YOU_COPY: Record<
  QuoteScoreAudience,
  { counterparty: string; inNetwork: string }
> = {
  carrier: {
    counterparty: "this shipper",
    // Best-guess copy — the carrier mockup's one example is not in the
    // carrier's network, so this line was never demonstrated.
    inNetwork: "You are in this shipper's network",
  },
  shipper: { counterparty: "you", inNetwork: "In your network" },
};

export function formatHistoryWithYou(
  historyFactor: QuoteScoreFactor,
  audience: QuoteScoreAudience,
): string[] {
  const lines: string[] = [];
  const { counterparty, inNetwork } = HISTORY_WITH_YOU_COPY[audience];
  const loadsWithShipper = getMetric(
    historyFactor,
    "loads_booked_with_shipper",
  );
  const networkMembership = getMetric(historyFactor, "network_membership");

  if (loadsWithShipper != null && typeof loadsWithShipper.value === "number") {
    const count = loadsWithShipper.value;
    lines.push(
      count === 0
        ? `No loads booked with ${counterparty} yet`
        : `${count} ${count === 1 ? "load" : "loads"} booked with ${counterparty}`,
    );
  }

  if (networkMembership?.value === true) {
    lines.push(inNetwork);
  }

  return lines;
}

export function formatHistoryAcrossNetwork(
  historyFactor: QuoteScoreFactor,
): string[] {
  const loadsBooked = getMetric(historyFactor, "loads_booked");
  if (loadsBooked == null || typeof loadsBooked.value !== "number") return [];

  const count = loadsBooked.value;
  return [`${count} total ${count === 1 ? "load" : "loads"} booked on Evotrux`];
}

export interface QuoteScoreRating {
  value: number;
  reviewCount: number;
  satisfactionPercent: number;
  satisfactionCount: number;
}

export function formatHistoryRating(
  historyFactor: QuoteScoreFactor,
): QuoteScoreRating | undefined {
  const starRating = getMetric(historyFactor, "star_rating");
  const satisfactionRating = getMetric(historyFactor, "satisfaction_rating");

  const reviewCount = starRating?.sample_size ?? 0;
  const satisfactionCount = satisfactionRating?.sample_size ?? 0;
  // Neither rating has enough of a sample to say anything — omit the whole
  // line rather than show "0.00 (0) · 0% (0)" for a brand-new carrier.
  if (reviewCount === 0 && satisfactionCount === 0) return undefined;

  return {
    value: typeof starRating?.value === "number" ? starRating.value : 0,
    reviewCount,
    satisfactionPercent:
      typeof satisfactionRating?.value === "number"
        ? Math.round(satisfactionRating.value * 100)
        : 0,
    satisfactionCount,
  };
}

// The seven Profile fields (§7) in the order the API returns them. Only three
// of these ("Valid insurance", "Emergency contact", and the label-derived
// "Profile complete") appear in either mockup — those were the shipper's
// narrower three-line view, not the carrier's own full checklist (§14). The
// other five labels are this file's own best guess.
const PROFILE_METRIC_LABELS: Record<string, string> = {
  equipment_types: "Equipment types",
  service_areas: "Service areas",
  insurance_current: "Valid insurance",
  fleet_size: "Fleet size",
  carrier_type: "Carrier type",
  emergency_contact_phone: "Emergency contact",
  company_description: "Company description",
};

export interface QuoteScoreProfileCheck {
  label: string;
  ok: boolean;
}

export function formatProfileChecks(
  profileFactor: QuoteScoreFactor,
  audience: QuoteScoreAudience,
): QuoteScoreProfileCheck[] {
  const checks = profileFactor.metrics.map((metric) => ({
    label: PROFILE_METRIC_LABELS[metric.name] ?? metric.name,
    ok: metric.value === true,
  }));
  if (audience === "carrier") {
    return checks;
  }
  // The shipper's response carries only two of the seven fields; the third
  // line is derived, since only all seven filled makes the label Very
  // competitive (docs/engineering/design/quote_competitiveness_scoring_design.md §7, §14).
  return [
    ...checks,
    {
      label: "Profile complete",
      ok: profileFactor.label === "very_competitive",
    },
  ];
}

export function findFactor(
  result: QuoteScoreResult | ShipperQuoteScore,
  factorName: QuoteScoreFactor["factor"],
): QuoteScoreFactor {
  const factor = result.factors.find((f) => f.factor === factorName);
  if (factor == null) {
    throw new Error(`Missing "${factorName}" factor in quote score result`);
  }
  return factor;
}
